/* src/ui/windows/fixturePanel.ts — press E on a fixture, manage it here.
 *
 * One window, three faces. A SHELF: choose what it sells from the building's storage, set your
 * price, see the market's for comparison. A STORAGE rack: what is in it, and the order form —
 * goods bought here are real market trades that arrive by truck, minutes later, into this
 * building. A CHECKOUT: what it does and how many customers it can take.
 *
 * Every number shown is the server's (the market row, the storage ledger, the delivery ETA);
 * every action is an RPC the composition root passed in as hooks. The panel re-paints from the
 * fresh Parcel each hook resolves with, and the caller re-points the 3D interior the same way —
 * so the room and the panel can never disagree about what is on the shelf.
 */

import type { Fixture, Parcel } from '../../core/types.ts';
import { gameStore } from '../../core/store.ts';
import { confirmDialog } from '../confirm.ts';
import { el } from '../dom.ts';
import { formatMoney } from '../format.ts';
import { toast } from '../toast.ts';
import type { WindowManager } from './windowChrome.ts';

const WINDOW_ID = 'fixture';

export interface FixtureHooks {
  onStock: (fixtureId: string, itemId: string, price: number) => Promise<Parcel | null>;
  onClear: (fixtureId: string) => Promise<Parcel | null>;
  onRemove: (fixtureId: string) => Promise<Parcel | null>;
  onOrder: (parcelId: string, itemId: string, qty: number) => Promise<Parcel | null>;
}

export interface FixturePanel {
  show: (parcel: Parcel, fixtureId: string) => void;
  close: () => void;
}

function marketName(itemId: string): string {
  return gameStore.get()?.market.find((m) => m.key === itemId)?.name ?? itemId;
}

function marketPrice(itemId: string): number {
  return gameStore.get()?.market.find((m) => m.key === itemId)?.price ?? 0;
}

export function openFixturePanel(
  windows: WindowManager,
  root: HTMLElement,
  parcel: Parcel,
  fixtureId: string,
  hooks: FixtureHooks,
): FixturePanel {
  const existing = windows.get(WINDOW_ID);
  if (existing) {
    const api = existing.userData as FixturePanel | undefined;
    if (api) {
      api.show(parcel, fixtureId);
      existing.focus();
      return api;
    }
  }

  let current: Parcel = parcel;
  let currentFixture = fixtureId;
  let busy = false;

  const win = windows.open({ id: WINDOW_ID, title: 'Fixture', width: 330 });
  const body = el('div', { class: 'gf-fx' });
  const status = el('p', { class: 'gf-fx-status' });
  win.body.replaceChildren(body, status);

  const fixture = (): Fixture | null =>
    current.fixtures.find((f) => f.id === currentFixture) ?? null;

  const run = async (label: string, act: () => Promise<Parcel | null>): Promise<void> => {
    if (busy) return;
    busy = true;
    status.textContent = label;
    try {
      const fresh = await act();
      if (fresh) current = fresh;
      status.textContent = '';
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'That did not work.';
      status.textContent = msg;
      toast(msg);
    } finally {
      busy = false;
      paint();
    }
  };

  function paintShelf(f: Fixture): void {
    const storageItems = Object.entries(current.storage?.items ?? {});
    const options = [...storageItems];
    // The shelf's current item stays offered even if storage ran dry — clearing it is explicit.
    if (f.itemId && !options.some(([id]) => id === f.itemId)) options.unshift([f.itemId, 0]);

    const select = el('select', { class: 'gf-fx-select' }) as HTMLSelectElement;
    for (const [id, qty] of options) {
      const opt = el('option', { attrs: { value: id } },
        `${marketName(id)} — ${String(Math.round(qty))} in storage`);
      select.appendChild(opt);
    }
    if (f.itemId) select.value = f.itemId;

    const priceInput = el('input', {
      class: 'gf-fx-price',
      attrs: { type: 'number', step: '0.05', min: '0.05' },
    }) as HTMLInputElement;
    const marketLine = el('span', { class: 'gf-fx-mkt' });
    const syncMarketLine = (): void => {
      const id = select.value;
      const mkt = marketPrice(id);
      marketLine.textContent = mkt > 0 ? `market ${formatMoney(mkt)}` : '';
      if (!priceInput.value && mkt > 0) priceInput.value = (Math.round(mkt * 1.4 * 20) / 20).toFixed(2);
    };
    select.addEventListener('change', () => {
      priceInput.value = '';
      syncMarketLine();
    });
    if (f.price !== null) priceInput.value = f.price.toFixed(2);
    syncMarketLine();

    body.replaceChildren(
      el('h4', { class: 'gf-fx-head' }, 'SHELF'),
      f.itemId
        ? el('p', { class: 'gf-fx-line' },
            `Selling ${marketName(f.itemId)} — ${String(Math.round(f.qty))} on the shelf`)
        : el('p', { class: 'gf-fx-line' }, 'Empty. Point it at something in storage.'),
      storageItems.length === 0 && !f.itemId
        ? el('p', { class: 'gf-fx-note' },
            'Nothing in storage yet — use a storage rack to order stock first.')
        : el('div', { class: 'gf-fx-form' },
            select,
            el('label', { class: 'gf-fx-pricerow' },
              el('span', null, 'Your price'), priceInput, marketLine),
            el('button', {
              class: 'gf-fx-act is-primary',
              attrs: { type: 'button' },
              on: {
                click: () => {
                  const price = Number(priceInput.value);
                  if (!Number.isFinite(price) || price <= 0) {
                    status.textContent = 'Set a price first.';
                    return;
                  }
                  void run('Stocking…', () => hooks.onStock(f.id, select.value, price));
                },
              },
            }, 'STOCK FROM STORAGE'),
          ),
      el('div', { class: 'gf-fx-actions' },
        f.itemId
          ? el('button', {
              class: 'gf-fx-act',
              attrs: { type: 'button' },
              on: { click: () => void run('Clearing…', () => hooks.onClear(f.id)) },
            }, 'CLEAR SHELF')
          : null,
        el('button', {
          class: 'gf-fx-act is-danger',
          attrs: { type: 'button' },
          on: {
            click: () => {
              void (async () => {
                const ok = await confirmDialog(root, {
                  title: 'Remove this shelf?',
                  body: 'The shelf goes back to your inventory as an item; its stock returns to storage.',
                  confirmLabel: 'Remove',
                  destructive: true,
                });
                if (ok) await run('Removing…', () => hooks.onRemove(f.id));
              })();
            },
          },
        }, 'REMOVE'),
      ),
    );
  }

  function paintStorage(f: Fixture): void {
    const storage = current.storage;
    const items = Object.entries(storage?.items ?? {});
    const market = gameStore.get()?.market ?? [];

    const select = el('select', { class: 'gf-fx-select' }) as HTMLSelectElement;
    for (const m of market) {
      select.appendChild(el('option', { attrs: { value: m.key } }, `${m.name} — ${formatMoney(m.price)}`));
    }
    const qtyInput = el('input', {
      class: 'gf-fx-qty',
      attrs: { type: 'number', step: '1', min: '1', value: '20' },
    }) as HTMLInputElement;
    const estimate = el('span', { class: 'gf-fx-mkt' });
    const syncEstimate = (): void => {
      const qty = Math.max(0, Number(qtyInput.value) || 0);
      const price = marketPrice(select.value);
      estimate.textContent = qty > 0 && price > 0
        ? `≈ ${formatMoney(price * qty)} + truck`
        : '';
    };
    select.addEventListener('change', syncEstimate);
    qtyInput.addEventListener('input', syncEstimate);
    syncEstimate();

    body.replaceChildren(
      el('h4', { class: 'gf-fx-head' }, 'STORAGE'),
      el('p', { class: 'gf-fx-line' },
        storage
          ? `${String(Math.round(storage.used))} / ${String(Math.round(storage.capacity))} units held`
          : 'No storage.'),
      items.length > 0
        ? el('ul', { class: 'gf-fx-list' },
            ...items.map(([id, qty]) =>
              el('li', null, `${marketName(id)} — ${String(Math.round(qty))}`)))
        : el('p', { class: 'gf-fx-note' }, 'Empty racks. Order below; a truck brings it here.'),
      ...(current.deliveries.length > 0
        ? [el('ul', { class: 'gf-fx-list is-pending' },
            ...current.deliveries.map((d) => {
              const mins = Math.max(0, (new Date(d.arrivesAt).getTime() - Date.now()) / 60000);
              return el('li', null,
                `🚚 ${String(Math.round(d.qty))} × ${marketName(d.itemId)} — ${mins < 1 ? 'arriving' : `~${String(Math.ceil(mins))} min`}`);
            }))]
        : []),
      el('div', { class: 'gf-fx-form' },
        select,
        el('label', { class: 'gf-fx-pricerow' }, el('span', null, 'Quantity'), qtyInput, estimate),
        el('button', {
          class: 'gf-fx-act is-primary',
          attrs: { type: 'button' },
          on: {
            click: () => {
              const qty = Number(qtyInput.value);
              if (!Number.isFinite(qty) || qty <= 0) {
                status.textContent = 'How many?';
                return;
              }
              void run('Ordering…', () => hooks.onOrder(current.id, select.value, qty));
            },
          },
        }, 'ORDER STOCK'),
      ),
      el('div', { class: 'gf-fx-actions' },
        el('button', {
          class: 'gf-fx-act is-danger',
          attrs: { type: 'button' },
          on: {
            click: () => {
              void (async () => {
                const ok = await confirmDialog(root, {
                  title: 'Remove this rack?',
                  body: 'Only an empty rack can come out; it returns to your inventory as an item.',
                  confirmLabel: 'Remove',
                  destructive: true,
                });
                if (ok) await run('Removing…', () => hooks.onRemove(f.id));
              })();
            },
          },
        }, 'REMOVE'),
      ),
    );
  }

  function paintCheckout(f: Fixture): void {
    body.replaceChildren(
      el('h4', { class: 'gf-fx-head' }, 'CHECKOUT'),
      el('p', { class: 'gf-fx-line' }, 'Takes up to 6 customers a minute.'),
      el('p', { class: 'gf-fx-note' },
        'A shop trades once it has a checkout and at least one stocked, priced shelf.'),
      el('div', { class: 'gf-fx-actions' },
        el('button', {
          class: 'gf-fx-act is-danger',
          attrs: { type: 'button' },
          on: {
            click: () => {
              void (async () => {
                const ok = await confirmDialog(root, {
                  title: 'Remove the checkout?',
                  body: 'The shop stops trading until another checkout goes in.',
                  confirmLabel: 'Remove',
                  destructive: true,
                });
                if (ok) await run('Removing…', () => hooks.onRemove(f.id));
              })();
            },
          },
        }, 'REMOVE'),
      ),
    );
  }

  function paint(): void {
    const f = fixture();
    if (!f) {
      // Removed (possibly by the very action that just ran): nothing left to manage.
      win.close();
      return;
    }
    win.setTitle(
      f.kind === 'shelf' ? 'Shelf' : f.kind === 'checkout' ? 'Checkout' : 'Storage rack',
    );
    if (f.kind === 'shelf') paintShelf(f);
    else if (f.kind === 'storage') paintStorage(f);
    else paintCheckout(f);
  }

  const api: FixturePanel = {
    show(nextParcel: Parcel, nextFixture: string): void {
      current = nextParcel;
      currentFixture = nextFixture;
      status.textContent = '';
      paint();
    },
    close(): void {
      win.close();
    },
  };
  win.userData = api;
  paint();
  return api;
}
